import React, { forwardRef, PropsWithChildren } from "react";
import { clsx } from "clsx";

import { DropdownMenu as RadixDropdownMenu } from "radix-ui";
import { Link as RouterLink } from "react-router-dom";
import { ChevronDown, ExternalLink } from "lucide-react";

import { twMerge } from "@/design-system/twMerge";

import { CheckboxGlyph } from "@/design-system/components/Checkbox/Checkbox";
import { Icon } from "@/design-system/components/Icon/Icon";
import { ConditionalTooltip } from "../Tooltip/Tooltip";

type DropdownMenuProps<T> = PropsWithChildren<{
  options: T[];
  getOptionLabel: (opt: T) => string;
  getIconBefore?: (opt: T) => React.ReactNode | undefined;
  getIconAfter?: (opt: T) => boolean | undefined;
  getOptionUrl?: (opt: T) => string | undefined;
  /** In-app route for an option; the whole row renders as a router link. */
  getOptionLinkTo?: (opt: T) => string | undefined;
  getIsDestructive?: (opt: T) => boolean | undefined;
  onSelectOption?: (opt: T) => void;
  icon?: React.ReactNode;
  /**
   * Replaces the default icon button. The element must forward its ref and
   * spread unknown props, since Radix merges trigger handlers into it.
   */
  trigger?: React.ReactElement;
  tooltipContent?: React.ReactNode;
  contentProps?: React.ComponentProps<typeof RadixDropdownMenu.Content>;
  rootProps?: React.ComponentProps<typeof RadixDropdownMenu.Root>;
  itemProps?: React.ComponentProps<typeof RadixDropdownMenu.Item>;
  triggerProps?: React.ComponentProps<typeof RadixDropdownMenu.Trigger>;
}>;

export function DropdownMenu<T>({
  children,
  options,
  getOptionLabel,
  getIconBefore,
  getIconAfter,
  getOptionUrl,
  getOptionLinkTo,
  getIsDestructive,
  onSelectOption,
  icon = <ChevronDown />,
  trigger,
  tooltipContent,
  contentProps,
  rootProps,
  itemProps,
  triggerProps,
}: DropdownMenuProps<T>) {
  return (
    <RadixDropdownMenu.Root {...rootProps}>
      <ConditionalTooltip condition={!!tooltipContent} content={tooltipContent}>
        {trigger ? (
          <RadixDropdownMenu.Trigger asChild {...triggerProps}>
            {trigger}
          </RadixDropdownMenu.Trigger>
        ) : (
          <RadixDropdownMenu.Trigger asChild>
            <button
              {...triggerProps}
              className={clsx(
                twMerge(
                  "hover:bg-surface-selected flex h-12 w-12 items-center justify-center rounded-lg p-2 hover:cursor-pointer",
                  triggerProps?.className,
                ),
              )}
              aria-label={triggerProps?.["aria-label"] ?? "Dropdown menu"}
            >
              {icon}
            </button>
          </RadixDropdownMenu.Trigger>
        )}
      </ConditionalTooltip>

      <RadixDropdownMenu.Portal>
        <RadixDropdownMenu.Content
          {...contentProps}
          className={clsx(
            twMerge(
              "bg-surface-default z-popover shadow-elevation-200 border-interactive-selected relative rounded-lg border",
              contentProps?.className,
            ),
          )}
        >
          {children ??
            options.map((option: T, index: number) => {
              const iconBefore = getIconBefore?.(option);
              const iconAfter = getIconAfter?.(option);
              const optionUrl = getOptionUrl?.(option);
              const linkTo = getOptionLinkTo?.(option);
              const isDestructive = getIsDestructive?.(option);

              const content = (
                <>
                  <div className="flex items-center gap-2">
                    {iconBefore && (
                      <span className="[&>svg]:!text-iconDefault flex-shrink-0">
                        {iconBefore}
                      </span>
                    )}
                    {optionUrl ? (
                      <a
                        href={optionUrl}
                        {...(iconAfter && {
                          target: "_blank",
                          rel: "noopener noreferrer",
                        })}
                        className="text-textDefault block no-underline"
                      >
                        {getOptionLabel(option)}
                      </a>
                    ) : (
                      <span>{getOptionLabel(option)}</span>
                    )}
                  </div>
                  {iconAfter && (
                    <span className="ml-2 flex-shrink-0">
                      <Icon
                        icon={ExternalLink}
                        size="lg"
                        className="text-iconMinimal"
                      />
                    </span>
                  )}
                </>
              );

              return (
                <RadixDropdownMenu.Item
                  {...itemProps}
                  asChild={!!linkTo}
                  className={clsx(
                    twMerge(
                      "hover:bg-surface-selected focus:bg-surface-selected flex items-center justify-between px-4 py-3 first:rounded-t-md last:rounded-b-md hover:cursor-pointer hover:!border-none hover:outline-0 focus:outline-0",
                      itemProps?.className,
                      linkTo && "text-textDefault no-underline",
                      isDestructive &&
                        "text-textError hover:bg-status-error-tint focus:bg-status-error-tint",
                    ),
                  )}
                  key={index}
                  onSelect={() => onSelectOption?.(option)}
                >
                  {linkTo ? (
                    <RouterLink to={linkTo}>{content}</RouterLink>
                  ) : (
                    content
                  )}
                </RadixDropdownMenu.Item>
              );
            })}
        </RadixDropdownMenu.Content>
      </RadixDropdownMenu.Portal>
    </RadixDropdownMenu.Root>
  );
}

type DropdownMenuCheckboxItemProps = Omit<
  React.ComponentProps<typeof RadixDropdownMenu.CheckboxItem>,
  "checked" | "onCheckedChange" | "onSelect"
> & {
  checked: boolean;
  onToggle: () => void;
};

/** A checkbox row that toggles without closing the menu. */
export function DropdownMenuCheckboxItem({
  checked,
  onToggle,
  className,
  children,
  ...rest
}: DropdownMenuCheckboxItemProps) {
  return (
    <RadixDropdownMenu.CheckboxItem
      {...rest}
      checked={checked}
      onSelect={(event) => {
        event.preventDefault();
        onToggle();
      }}
      className={twMerge(
        "text-body-sm text-textDefault hover:bg-surface-selected focus:bg-surface-selected flex cursor-pointer items-center gap-2 px-4 py-2 outline-none data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50",
        className,
      )}
    >
      <CheckboxGlyph checked={checked} className="size-5" />
      {children}
    </RadixDropdownMenu.CheckboxItem>
  );
}

export function DropdownMenuLabel({
  className,
  ...rest
}: React.ComponentProps<typeof RadixDropdownMenu.Label>) {
  return (
    <RadixDropdownMenu.Label
      {...rest}
      className={twMerge(
        "text-label-xs text-textSubtle px-4 pt-3 pb-1",
        className,
      )}
    />
  );
}

/**
 * A bordered, select-style box with a trailing chevron, for use as a
 * DropdownMenu `trigger`. It is a `div` with the button role so content such
 * as removable chips can hold their own buttons.
 */
export const DropdownMenuSelectTrigger = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...rest }, ref) => (
  <div
    ref={ref}
    role="button"
    tabIndex={0}
    {...rest}
    className={twMerge(
      "border-dividerBold text-body-sm text-textDefault bg-surface-default focus:border-interactive-selected data-[state=open]:border-interactive-selected flex h-10 w-full cursor-pointer items-center justify-between gap-2 rounded-lg border px-3 outline-none",
      className,
    )}
  >
    {children}
    <ChevronDown size={20} className="text-textSubtle shrink-0" />
  </div>
));
DropdownMenuSelectTrigger.displayName = "DropdownMenuSelectTrigger";
